import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type VisualizerSource = 'agent' | 'user';

@customElement('cp-audio-visualizer')
export class CpAudioVisualizer extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) source: VisualizerSource = 'agent';
  @property({ type: Boolean }) active = false;

  @state() private _canvasWidth = 300;
  @state() private _canvasHeight = 80;

  private _canvas: HTMLCanvasElement | null = null;
  private _ctx: CanvasRenderingContext2D | null = null;
  private _animationFrame = 0;
  private _analyser: AnalyserNode | null = null;
  private _audioContext: AudioContext | null = null;
  private _dataArray: Uint8Array<ArrayBuffer> | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this._canvasWidth = entry.contentRect.width;
        this._canvasHeight = entry.contentRect.height;
        if (this._canvas) {
          this._canvas.width = this._canvasWidth;
          this._canvas.height = this._canvasHeight;
        }
      }
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopAnimation();
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }

  firstUpdated(): void {
    this._canvas = this.querySelector('canvas');
    if (this._canvas) {
      this._ctx = this._canvas.getContext('2d');
      this._resizeObserver?.observe(this._canvas.parentElement ?? this);
    }
  }

  updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('active')) {
      if (this.active) {
        this._startAnimation();
      } else {
        this._stopAnimation();
      }
    }
  }

  /**
   * Connect an AnalyserNode from an external AudioContext.
   * Call this after the audio pipeline is set up.
   */
  connectAnalyser(analyser: AnalyserNode): void {
    this._analyser = analyser;
    this._analyser.fftSize = 256;
    this._dataArray = new Uint8Array(this._analyser.frequencyBinCount);
  }

  /**
   * Create an AnalyserNode from a MediaStream (e.g. user microphone).
   */
  connectStream(stream: MediaStream): void {
    this._audioContext = new AudioContext();
    const source = this._audioContext.createMediaStreamSource(stream);
    this._analyser = this._audioContext.createAnalyser();
    this._analyser.fftSize = 256;
    source.connect(this._analyser);
    this._dataArray = new Uint8Array(this._analyser.frequencyBinCount);
  }

  disconnect(): void {
    this._stopAnimation();
    if (this._audioContext) {
      this._audioContext.close().catch(() => {});
      this._audioContext = null;
    }
    this._analyser = null;
    this._dataArray = null;
  }

  private _startAnimation(): void {
    if (this._animationFrame) return;
    this._draw();
  }

  private _stopAnimation(): void {
    if (this._animationFrame) {
      cancelAnimationFrame(this._animationFrame);
      this._animationFrame = 0;
    }
    // Clear canvas
    if (this._ctx && this._canvas) {
      this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }
  }

  private _draw = (): void => {
    this._animationFrame = requestAnimationFrame(this._draw);

    if (!this._ctx || !this._canvas) return;

    const ctx = this._ctx;
    const width = this._canvas.width;
    const height = this._canvas.height;

    ctx.clearRect(0, 0, width, height);

    if (!this._analyser || !this._dataArray) {
      // No analyser - draw idle state
      this._drawIdle(ctx, width, height);
      return;
    }

    this._analyser.getByteFrequencyData(this._dataArray);

    // Check if there's actual audio (not just noise floor)
    let sum = 0;
    for (let i = 0; i < this._dataArray.length; i++) {
      sum += this._dataArray[i];
    }
    const avg = sum / this._dataArray.length;

    if (avg < 5) {
      this._drawIdle(ctx, width, height);
      return;
    }

    this._drawWaveform(ctx, width, height, this._dataArray);
  };

  private _drawIdle(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const centerY = height / 2;
    const barCount = 32;
    const barWidth = Math.max(2, (width / barCount) * 0.6);
    const gap = width / barCount;
    const time = Date.now() / 1000;

    ctx.fillStyle = this.source === 'agent'
      ? 'rgba(99, 102, 241, 0.3)'
      : 'rgba(34, 197, 94, 0.3)';

    for (let i = 0; i < barCount; i++) {
      const x = i * gap + (gap - barWidth) / 2;
      const idleHeight = 2 + Math.sin(time * 2 + i * 0.3) * 2;
      ctx.fillRect(x, centerY - idleHeight / 2, barWidth, idleHeight);
    }
  }

  private _drawWaveform(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    data: Uint8Array<ArrayBuffer>,
  ): void {
    const centerY = height / 2;
    const barCount = Math.min(data.length, 64);
    const barWidth = Math.max(2, (width / barCount) * 0.6);
    const gap = width / barCount;
    const maxBarHeight = height * 0.8;

    const color = this.source === 'agent'
      ? 'rgba(99, 102, 241, 0.8)'
      : 'rgba(34, 197, 94, 0.8)';
    const colorDim = this.source === 'agent'
      ? 'rgba(99, 102, 241, 0.4)'
      : 'rgba(34, 197, 94, 0.4)';

    for (let i = 0; i < barCount; i++) {
      const value = data[i] / 255;
      const barHeight = Math.max(2, value * maxBarHeight);
      const x = i * gap + (gap - barWidth) / 2;

      // Gradient effect: center bars brighter
      const centerRatio = 1 - Math.abs(i - barCount / 2) / (barCount / 2);
      ctx.fillStyle = centerRatio > 0.5 ? color : colorDim;

      // Draw bar centered vertically with rounded ends
      const radius = barWidth / 2;
      const y = centerY - barHeight / 2;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, radius);
      ctx.fill();
    }
  }

  render() {
    return html`
      <div class="cp-audio-visualizer cp-audio-visualizer--${this.source}${this.active ? ' cp-audio-visualizer--active' : ''}">
        <canvas width="${this._canvasWidth}" height="${this._canvasHeight}"></canvas>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-audio-visualizer': CpAudioVisualizer;
  }
}
